/**
 * Adressvorschläge für Empfänger- und Teilnehmerfelder — geteilt zwischen
 * dem Mail-Verfassen-Fenster (An/Cc/Bcc) und dem Termin-Fenster (Teilnehmer).
 *
 * Zwei Quellen, beide über `GET /contact/email-recipients`:
 *  - Kontextgruppe (T-22/T-256): die Adressen der Kontakte, um die es geht
 *    (Kunde/Hauptkontakt plus weitere Kontakte, z. B. alle Kontakte des
 *    Vorgangs; bei einer Firma auch Zentrale und Ansprechpartner). Sie stehen
 *    schon beim Klick ins Feld bereit, ohne Tippen.
 *  - Tippsuche: ab 2 Zeichen alle in KMVS hinterlegten Adressen (Name,
 *    E-Mail, Firma), Kontexttreffer vorn.
 *
 * Bereits gewählte Adressen erscheinen nicht noch einmal. Die Auswahl geht per
 * Maus und per Tastatur (Pfeil hoch/runter markiert, Enter übernimmt — das
 * erledigt der Aufrufer über `markierterTreffer`).
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { tokens } from "@fluentui/react-components";
import { graphMailService, type EmailRecipientHit } from "../../services/graphMailService";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export interface EmpfaengerVorschlaegeOptionen {
    /** Schaltet die Vorschläge überhaupt frei (Mail: `enableContactSearch`). */
    aktiv: boolean;
    /** Ist die Liste gerade offen (Feld fokussiert/angeklickt)? */
    offen: boolean;
    /** Aktueller Text im Eingabefeld. */
    eingabe: string;
    /** Kunde bzw. Hauptkontakt — seine Adressen kommen zuerst. */
    kontextContactId?: number | null;
    /** Weitere Kontakte der Kontextgruppe (z. B. alle Vorgangskontakte). */
    kontextContactIds?: readonly number[];
    /** Schon gewählte Adressen — sie werden nicht noch einmal vorgeschlagen. */
    bereitsGewaehlt: readonly string[];
    /** Entprellung der Tippsuche in ms (Default 300). */
    debounceMs?: number;
}

export interface EmpfaengerVorschlaege {
    /** Gibt es eine Kontextgruppe (Kunde/Kontakte)? */
    hatKontext: boolean;
    /** Wird gerade gesucht (≥ 2 Zeichen getippt)? */
    tippt: boolean;
    /** Soll die Liste erscheinen (Tippsuche oder Kontextgruppe)? */
    zeigen: boolean;
    /** Lädt die gerade gezeigte Quelle noch? */
    laedt: boolean;
    /** Gab es überhaupt Kontexttreffer (für den Leer-Hinweis)? */
    kontextVorhanden: boolean;
    /** Die anzuzeigenden Treffer (ohne schon gewählte). */
    treffer: EmailRecipientHit[];
    /** Per Tastatur markierter Index, -1 = keiner. */
    markiert: number;
    setMarkiert: (i: number) => void;
    /** Der markierte Treffer oder null. */
    markierterTreffer: EmailRecipientHit | null;
    /**
     * Pfeiltasten auswerten. Liefert true, wenn die Taste verbraucht wurde
     * (dann `preventDefault` beim Aufrufer).
     */
    pfeilTaste: (key: string) => boolean;
}

/** Schlüssel der Kontextgruppe — null, wenn es keine gibt. */
export function kontextSchluessel(
    kontextContactId: number | null | undefined,
    kontextContactIds: readonly number[] | undefined,
): string | null {
    const weitere = (kontextContactIds ?? []).filter((id) => id !== kontextContactId);
    if (kontextContactId == null && weitere.length === 0) return null;
    return [kontextContactId ?? "", ...weitere].join(",");
}

/** Treffer ohne die schon gewählten Adressen (Groß-/Kleinschreibung egal). */
export function ohneGewaehlte(
    treffer: readonly EmailRecipientHit[],
    bereitsGewaehlt: readonly string[],
): EmailRecipientHit[] {
    const gewaehlt = new Set(bereitsGewaehlt.map((r) => r.trim().toLowerCase()));
    return treffer.filter((h) => !gewaehlt.has(h.email.trim().toLowerCase()));
}

/** Nächster markierter Index bei Pfeil hoch/runter (zyklisch, -1 = keiner). */
export function naechsteMarkierung(aktuell: number, anzahl: number, richtung: 1 | -1): number {
    if (anzahl <= 0) return -1;
    if (aktuell < 0) return richtung === 1 ? 0 : anzahl - 1;
    return (aktuell + richtung + anzahl) % anzahl;
}

export function useEmpfaengerVorschlaege(opts: EmpfaengerVorschlaegeOptionen): EmpfaengerVorschlaege {
    const { aktiv, offen, eingabe, kontextContactId = null, kontextContactIds, bereitsGewaehlt, debounceMs = 300 } = opts;
    // T-256: Die Kontextgruppe hängt am Kunden UND den weiteren Kontakten —
    // ein Schlüssel für beide, damit sich der Merker nicht verschluckt.
    const schluessel = kontextSchluessel(kontextContactId, kontextContactIds);
    const hatKontext = schluessel != null;
    const weitere = (kontextContactIds ?? []).filter((id) => id !== kontextContactId);
    const kontextOpts = {
        kontextContactId,
        ...(weitere.length > 0 ? { kontextContactIds: weitere } : {}),
    };

    const [hits, setHits] = useState<EmailRecipientHit[]>([]);
    const [loading, setLoading] = useState(false);
    const debounced = useDebouncedValue(eingabe, debounceMs);

    // T-22: Vorschläge zum Kontext — einmal je Kontext geladen und gemerkt,
    // nicht bei jedem Klick ins Feld neu geholt.
    const [kontextHits, setKontextHits] = useState<EmailRecipientHit[]>([]);
    const [kontextLaedt, setKontextLaedt] = useState(false);
    const kontextGeladenFuer = useRef<string | null>(null);

    useEffect(() => {
        if (!aktiv || !offen || schluessel == null) return;
        if (kontextGeladenFuer.current === schluessel) return;
        const fuer = schluessel;
        kontextGeladenFuer.current = fuer;
        setKontextLaedt(true);
        void graphMailService
            .searchEmailRecipients("", kontextOpts)
            .then((res) => {
                if (kontextGeladenFuer.current !== fuer) return;
                setKontextHits(res.filter((h) => h.kontext));
            })
            .finally(() => {
                if (kontextGeladenFuer.current === fuer) setKontextLaedt(false);
            });
        // `weitere` steckt im Schlüssel — als Array wäre es bei jedem Rendern neu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, offen, schluessel]);

    // Entprellte Tippsuche (cancelled-Guard gegen Race bei schnellem Tippen).
    useEffect(() => {
        if (!aktiv || !offen) return;
        const q = debounced.trim();
        if (q.length < 2) {
            setHits([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const suche = hatKontext
            ? graphMailService.searchEmailRecipients(q, kontextOpts)
            : graphMailService.searchEmailRecipients(q);
        suche.then((res) => {
            if (!cancelled) {
                setHits(res);
                setLoading(false);
            }
        });
        return () => {
            cancelled = true;
            setLoading(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, offen, debounced, schluessel]);

    const tippt = eingabe.trim().length >= 2;
    const treffer = ohneGewaehlte(tippt ? hits : kontextHits, bereitsGewaehlt);
    // T-256: Auch eine LEERE Kontextgruppe wird angezeigt — mit Hinweis.
    const zeigen = aktiv && offen && (tippt || hatKontext);
    const laedt = tippt ? loading : kontextLaedt && treffer.length === 0;

    // Tastatur-Markierung: springt zurück, sobald sich die Liste ändert.
    const [markiert, setMarkiert] = useState(-1);
    const trefferSchluessel = treffer.map((h) => `${h.email}|${h.contactId}`).join(",");
    useEffect(() => {
        setMarkiert(-1);
    }, [trefferSchluessel, zeigen]);
    const markierterTreffer = zeigen && markiert >= 0 ? (treffer[markiert] ?? null) : null;

    const pfeilTaste = (key: string): boolean => {
        if (!zeigen || treffer.length === 0) return false;
        if (key === "ArrowDown") {
            setMarkiert((m) => naechsteMarkierung(m, treffer.length, 1));
            return true;
        }
        if (key === "ArrowUp") {
            setMarkiert((m) => naechsteMarkierung(m, treffer.length, -1));
            return true;
        }
        return false;
    };

    return {
        hatKontext,
        tippt,
        zeigen,
        laedt,
        kontextVorhanden: kontextHits.length > 0,
        treffer,
        markiert,
        setMarkiert,
        markierterTreffer,
        pfeilTaste,
    };
}

/** Überschrift einer Gruppe im Vorschlags-Dropdown (T-22). */
const gruppenKopfStyle: React.CSSProperties = {
    padding: "6px 12px 4px", fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    textTransform: "uppercase", color: tokens.colorNeutralForeground3,
    background: tokens.colorNeutralBackground2, cursor: "default",
};

const hinweisStyle: React.CSSProperties = {
    padding: "8px 12px", fontSize: 13, fontStyle: "italic", color: tokens.colorNeutralForeground3,
};

export interface EmpfaengerVorschlagTexte {
    /** Überschrift der Kontextgruppe. */
    kontextTitel: string;
    /** Hinweis: alle Kontextadressen sind schon gewählt. */
    alleGewaehlt: string;
    /** Hinweis: zum Kontext ist keine Adresse hinterlegt. */
    keineAdresse: string;
}

/** Texte des Mail-Fensters (T-22/T-256) — der Default. */
export const MAIL_VORSCHLAG_TEXTE: EmpfaengerVorschlagTexte = {
    kontextTitel: "Vorschläge zum Kunden",
    alleGewaehlt:
        "Alle Adressen zum Kunden sind schon eingetragen. Für weitere Empfänger Namen oder Adresse eintippen.",
    keineAdresse:
        "Zum Kunden ist keine E-Mail-Adresse hinterlegt. Namen oder Adresse eintippen, um im ganzen Bestand zu suchen.",
};

export interface EmpfaengerVorschlagsListeProps {
    vorschlaege: EmpfaengerVorschlaege;
    /** Element, unter dem die Liste aufgeht (Breite wird übernommen). */
    ankerRef: React.RefObject<HTMLElement | null>;
    onSelect: (hit: EmailRecipientHit) => void;
    texte?: EmpfaengerVorschlagTexte;
}

/**
 * Die Vorschlagsliste selbst — per Portal/fixed unter dem Anker, damit sie
 * nicht vom Fenster-Overflow abgeschnitten wird (Muster wie EntityPicker).
 */
export const EmpfaengerVorschlagsListe: React.FC<EmpfaengerVorschlagsListeProps> = ({
    vorschlaege,
    ankerRef,
    onSelect,
    texte = MAIL_VORSCHLAG_TEXTE,
}) => {
    const { zeigen, tippt, laedt, treffer, kontextVorhanden, markiert, setMarkiert } = vorschlaege;
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
    const listeRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!zeigen) return;
        const reposition = () => {
            const r = ankerRef.current?.getBoundingClientRect();
            if (r) setPos({ top: r.bottom + 2, left: r.left, width: r.width });
        };
        reposition();
        alleFenster.addEventListener("scroll", reposition, true);
        alleFenster.addEventListener("resize", reposition);
        return () => {
            alleFenster.removeEventListener("scroll", reposition, true);
            alleFenster.removeEventListener("resize", reposition);
        };
    }, [zeigen, ankerRef]);

    // Per Tastatur markierten Eintrag sichtbar halten.
    useEffect(() => {
        if (markiert < 0) return;
        const el = listeRef.current?.querySelector<HTMLElement>(`[data-vorschlag-index="${markiert}"]`);
        el?.scrollIntoView?.({ block: "nearest" });
    }, [markiert]);

    if (!zeigen || !pos) return null;

    return createPortal(
        <div
            ref={listeRef}
            role="listbox"
            // Klicks auf Überschrift oder Scrollleiste dürfen dem Eingabefeld
            // den Fokus nicht nehmen — sonst schlösse die Liste beim Scrollen.
            onMouseDown={(e) => e.preventDefault()}
            style={{
                position: "fixed", top: pos.top, left: pos.left, width: pos.width,
                maxHeight: 280, overflowY: "auto", zIndex: 100000,
                background: "#fff", border: `1px solid ${tokens.colorNeutralStroke2}`,
                borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
                fontFamily: "'Segoe UI', system-ui, sans-serif",
            }}
        >
            {laedt ? (
                <div style={hinweisStyle}>{tippt ? "Suche…" : "Vorschläge werden geladen…"}</div>
            ) : treffer.length === 0 ? (
                <div style={hinweisStyle}>
                    {tippt ? "Keine Treffer." : kontextVorhanden ? texte.alleGewaehlt : texte.keineAdresse}
                </div>
            ) : (
                treffer.map((h, i) => {
                    const vorher = treffer[i - 1];
                    const ersterKontext = h.kontext && !vorher?.kontext;
                    const ersterUebriger = !h.kontext && !!vorher?.kontext;
                    const aktiv = i === markiert;
                    return (
                        <React.Fragment key={`${h.email}-${h.contactId}-${h.companyName ?? ""}-${i}`}>
                            {ersterKontext && <div style={gruppenKopfStyle}>{texte.kontextTitel}</div>}
                            {ersterUebriger && (
                                <div style={{ ...gruppenKopfStyle, borderTop: `2px solid ${tokens.colorNeutralStroke2}` }}>
                                    Weitere Treffer
                                </div>
                            )}
                            <div
                                role="option"
                                aria-selected={aktiv}
                                data-empfaenger-vorschlag={h.email}
                                data-vorschlag-index={i}
                                // onMouseDown + preventDefault: verhindert, dass das
                                // input-onBlur→commit zuerst feuert und die Auswahl überschreibt.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onSelect(h);
                                }}
                                onMouseEnter={() => setMarkiert(i)}
                                style={{
                                    padding: "7px 12px", cursor: "pointer", fontSize: 13,
                                    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
                                    background: aktiv ? tokens.colorNeutralBackground1Hover : "transparent",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                    <span style={{ fontWeight: 600, color: tokens.colorNeutralForeground1 }}>{h.displayName}</span>
                                    <span style={{ color: tokens.colorNeutralForeground3, fontSize: 12 }}>{h.email}</span>
                                </div>
                                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                                    <span style={{
                                        fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 8,
                                        background: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
                                    }}>{h.kind === "geschaeftlich" ? "Geschäftlich" : "Privat"}</span>
                                    {h.companyName && (
                                        <span style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>{h.companyName}</span>
                                    )}
                                    {h.isExtern && (
                                        <span style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>extern</span>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })
            )}
        </div>,
        document.body,
    );
};
